import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus } from "lucide-react";

import { apiError } from "../../../shared/api/client";
import {
  PLAN_STATUS_LABELS,
  treatmentPlanQueryKeys,
  treatmentPlansApi,
  type TreatmentPlan,
} from "../../../shared/api/treatmentPlans";
import { formatDate } from "../../../shared/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Kế hoạch điều trị của một bệnh nhân (#18736) — every specialty, not only dental.
 *
 * <p>Dental plans appear here too, read-only. They are the same rows, but tooth-level editing lives
 * on the odontogram where the chart is; offering a second, weaker editor for them would let two
 * screens disagree about the same plan.
 */
export function TreatmentPlansSection({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");

  const plans = useQuery({
    queryKey: treatmentPlanQueryKeys.forPatient(patientId),
    queryFn: () => treatmentPlansApi.forPatient(patientId),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: treatmentPlanQueryKeys.forPatient(patientId) });

  const create = useMutation({
    mutationFn: () => treatmentPlansApi.create(patientId, title.trim(), note.trim() || null),
    onSuccess: () => {
      setTitle("");
      setNote("");
      setCreating(false);
      void invalidate();
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <ClipboardList className="size-4" />
          Kế hoạch điều trị
        </h2>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 size-4" />
            Lập kế hoạch
          </Button>
        )}
      </div>

      {creating && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label htmlFor="planTitle">Tên kế hoạch</Label>
              <Input
                id="planTitle"
                value={title}
                placeholder="VD: Điều trị viêm quanh răng 3 buổi"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="planNote">Ghi chú</Label>
              <Input id="planNote" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            {create.isError && (
              <p className="text-sm text-destructive">{apiError(create.error).detail}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCreating(false)}>
                Huỷ
              </Button>
              <Button size="sm" onClick={() => create.mutate()} disabled={!title.trim() || create.isPending}>
                {create.isPending ? "Đang lưu…" : "Tạo"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {plans.isLoading && <Skeleton className="h-24" />}
      {plans.isError && <p className="text-sm text-destructive">{apiError(plans.error).detail}</p>}
      {plans.data?.length === 0 && !creating && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Bệnh nhân chưa có kế hoạch điều trị nào.
        </p>
      )}

      {plans.data?.map((p) => (
        <PlanCard key={p.id} plan={p} onChanged={invalidate} />
      ))}
    </div>
  );
}

function PlanCard({ plan, onChanged }: { plan: TreatmentPlan; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState("");
  const [session, setSession] = useState<number | "">("");

  // Dental plans are edited on the odontogram, where the tooth chart is.
  const readOnly = plan.vertical === "DENTAL";

  const addItem = useMutation({
    mutationFn: () =>
      treatmentPlansApi.addItem(plan.id, desc.trim(), session === "" ? null : Number(session)),
    onSuccess: () => {
      setDesc("");
      setSession("");
      setAdding(false);
      onChanged();
    },
  });
  const complete = useMutation({
    mutationFn: (itemId: string) => treatmentPlansApi.completeItem(itemId),
    onSuccess: onChanged,
  });
  const setStatus = useMutation({
    mutationFn: (s: TreatmentPlan["status"]) => treatmentPlansApi.setStatus(plan.id, s),
    onSuccess: onChanged,
  });

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <strong className="text-sm">{plan.title}</strong>
            <span className="ml-2 text-xs text-muted-foreground">
              {formatDate(plan.createdAt)} · {plan.doneItems}/{plan.totalItems} hạng mục
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {readOnly && <Badge variant="secondary">Nha khoa</Badge>}
            <Badge>{PLAN_STATUS_LABELS[plan.status] ?? plan.status}</Badge>
          </div>
        </div>
        {plan.note && <p className="mb-2 text-xs text-muted-foreground">{plan.note}</p>}

        <ul className="divide-y text-sm">
          {plan.items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 py-1.5">
              <span className={it.status === "COMPLETED" ? "text-muted-foreground line-through" : ""}>
                {it.sessionNo != null && (
                  <span className="mr-1.5 text-xs text-muted-foreground">Buổi {it.sessionNo}</span>
                )}
                {it.description}
              </span>
              {it.status === "COMPLETED" ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {it.doneAt ? formatDate(it.doneAt) : "Đã xong"}
                </span>
              ) : (
                !readOnly && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => complete.mutate(it.id)}
                    disabled={complete.isPending}
                  >
                    Hoàn tất
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>

        {readOnly ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Kế hoạch nha khoa được sửa ở tab Nha khoa, nơi có sơ đồ răng.
          </p>
        ) : adding ? (
          <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="min-w-[200px] flex-1">
              <Label htmlFor={`d-${plan.id}`}>Hạng mục</Label>
              <Input id={`d-${plan.id}`} value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            <div className="w-24">
              <Label htmlFor={`s-${plan.id}`}>Buổi</Label>
              <Input
                id={`s-${plan.id}`}
                type="number"
                min={1}
                value={session}
                onChange={(e) => setSession(e.target.value === "" ? "" : Number(e.target.value))}
              />
            </div>
            <Button size="sm" onClick={() => addItem.mutate()} disabled={!desc.trim() || addItem.isPending}>
              Thêm
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
              Huỷ
            </Button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              Thêm hạng mục
            </Button>
            {plan.status === "ACTIVE" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStatus.mutate("COMPLETED")}
                disabled={setStatus.isPending}
              >
                Đánh dấu hoàn tất
              </Button>
            )}
          </div>
        )}
        {addItem.isError && (
          <p className="mt-1 text-sm text-destructive">{apiError(addItem.error).detail}</p>
        )}
      </CardContent>
    </Card>
  );
}
